import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { fileToBase64 } from "../../lib/imageUtils";
import { Trash2, Check, X, ArrowUp, ArrowDown, Pencil, LayoutGrid, Grid3x3 } from "lucide-react";
import { TILE_SIZE_OPTIONS, tileSpan } from "../../lib/gallerySizes";
import { FEATURE_ICONS } from "../../lib/featureIcons";

const STATUS_BADGE = {
  pending: { bg: "#FDF3E1", color: "#8A6200", label_mn: "Хүлээгдэж буй", label_en: "Pending" },
  approved: { bg: "#E2EEE6", color: "#1A5E35", label_mn: "Баталгаажсан", label_en: "Approved" },
  rejected: { bg: "#FCE7E3", color: "#8A1F1F", label_mn: "Татгалзсан", label_en: "Rejected" },
};

export function BookingsTab({ lang }) {
  const { push: toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/admin/bookings");
    setBookings(r.data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast(lang === "mn" ? "Төлөв шинэчлэгдлээ" : "Status updated");
      load();
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "mn" ? "Устгах уу?" : "Delete?")) return;
    await api.delete(`/admin/bookings/${id}`);
    load();
  };

  const filtered = bookings.filter((b) => filter === "all" || b.status === filter);

  return (
    <div className="space-y-5" data-testid="admin-bookings-tab">
      <div className="flex flex-wrap gap-2">
        {["all", "pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`btn btn-sm ${filter === s ? "btn-primary" : "btn-ghost"}`}
            data-testid={`bookings-filter-${s}`}
          >
            {s === "all" ? (lang === "mn" ? "Бүгд" : "All") : STATUS_BADGE[s][`label_${lang}`]}
            <span className="opacity-60 ml-1">({s === "all" ? bookings.length : bookings.filter((b) => b.status === s).length})</span>
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead style={{ background: "var(--muted)" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">{lang === "mn" ? "Нэр" : "Name"}</th>
                <th className="px-4 py-3 font-medium">{lang === "mn" ? "Утас" : "Phone"}</th>
                <th className="px-4 py-3 font-medium">{lang === "mn" ? "Байшин" : "House"}</th>
                <th className="px-4 py-3 font-medium">{lang === "mn" ? "Өдрүүд" : "Dates"}</th>
                <th className="px-4 py-3 font-medium">{lang === "mn" ? "Гүйлгээ" : "Txn"}</th>
                <th className="px-4 py-3 font-medium">{lang === "mn" ? "Төлөв" : "Status"}</th>
                <th className="px-4 py-3 font-medium text-right">{lang === "mn" ? "Үйлдэл" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const badge = STATUS_BADGE[b.status] || STATUS_BADGE.pending;
                return (
                  <tr key={b.id} className="border-t border-[color:var(--border)]" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-3 font-medium">{b.name}</td>
                    <td className="px-4 py-3">{b.phone}</td>
                    <td className="px-4 py-3">{lang === "mn" ? b.tiny_house_name_mn : b.tiny_house_name_en}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{b.check_in} → {b.check_out}</td>
                    <td className="px-4 py-3 max-w-[180px] truncate" title={b.transaction_text}>{b.transaction_text}</td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-block px-2.5 py-1 rounded-full text-xs"
                        style={{ background: badge.bg, color: badge.color }}
                      >
                        {badge[`label_${lang}`]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        {b.status !== "approved" && (
                          <button onClick={() => setStatus(b.id, "approved")} className="btn btn-sm" style={{ background: "#E2EEE6", color: "#1A5E35" }} data-testid={`approve-${b.id}`}>
                            <Check size={14} />
                          </button>
                        )}
                        {b.status !== "rejected" && (
                          <button onClick={() => setStatus(b.id, "rejected")} className="btn btn-sm" style={{ background: "#FCE7E3", color: "#8A1F1F" }} data-testid={`reject-${b.id}`}>
                            <X size={14} />
                          </button>
                        )}
                        <button onClick={() => del(b.id)} className="btn btn-sm btn-ghost" data-testid={`delete-booking-${b.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center opacity-60">
                    {lang === "mn" ? "Захиалга байхгүй" : "No bookings"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---- Tiny Houses CRUD ----
const EMPTY_HOUSE = {
  name_mn: "",
  name_en: "",
  description_mn: "",
  description_en: "",
  price_per_night: 0,
  capacity: 2,
  image_base64: null,
  active: true,
};

export function HousesTab({ lang }) {
  const { push: toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/tiny-houses");
    setItems(r.data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      const payload = { ...editing };
      delete payload.id;
      delete payload.created_at;
      delete payload.cover_url;
      if (editing.id) {
        await api.put(`/admin/tiny-houses/${editing.id}`, payload);
      } else {
        await api.post("/admin/tiny-houses", payload);
      }
      toast(lang === "mn" ? "Хадгаллаа" : "Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "mn" ? "Устгах уу?" : "Delete?")) return;
    await api.delete(`/admin/tiny-houses/${id}`);
    load();
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const b64 = await fileToBase64(f);
      setEditing({ ...editing, image_base64: b64 });
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-houses-tab">
      <div className="flex justify-end">
        <button className="btn btn-primary btn-sm" onClick={() => setEditing({ ...EMPTY_HOUSE })} data-testid="add-house-btn">
          + {lang === "mn" ? "Байшин нэмэх" : "Add house"}
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((h) => (
          <div key={h.id} className="card overflow-hidden" data-testid={`admin-house-${h.id}`}>
            <div className="aspect-[4/3] overflow-hidden bg-[color:var(--muted)]">
              {(h.image_base64 || h.cover_url) ? (
                <img
                  src={h.image_base64 ? `data:image/jpeg;base64,${h.image_base64}` : h.cover_url}
                  alt={h.name_en}
                  className="w-full h-full object-cover"
                />
              ) : null}
            </div>
            <div className="p-4">
              <div className="font-serif text-xl">{lang === "mn" ? h.name_mn : h.name_en}</div>
              <div className="text-xs opacity-60 mt-1">{h.price_per_night?.toLocaleString()} ₮ · {h.capacity}</div>
              <div className="flex gap-2 mt-3">
                <button className="btn btn-sm btn-ghost" onClick={() => setEditing({ ...h })} data-testid={`edit-house-${h.id}`}>
                  {lang === "mn" ? "Засах" : "Edit"}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => del(h.id)} data-testid={`delete-house-${h.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEditing(null)}>
          <div className="card max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl">{editing.id ? (lang === "mn" ? "Засах" : "Edit") : (lang === "mn" ? "Шинэ байшин" : "New house")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs opacity-60">Name (MN)</label>
                <input className="input-box" value={editing.name_mn} onChange={(e) => setEditing({ ...editing, name_mn: e.target.value })} data-testid="house-name-mn" />
              </div>
              <div>
                <label className="text-xs opacity-60">Name (EN)</label>
                <input className="input-box" value={editing.name_en} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} data-testid="house-name-en" />
              </div>
            </div>
            <div>
              <label className="text-xs opacity-60">Description (MN)</label>
              <textarea rows={2} className="input-box" value={editing.description_mn} onChange={(e) => setEditing({ ...editing, description_mn: e.target.value })} />
            </div>
            <div>
              <label className="text-xs opacity-60">Description (EN)</label>
              <textarea rows={2} className="input-box" value={editing.description_en} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs opacity-60">{lang === "mn" ? "Үнэ (₮)" : "Price (₮)"}</label>
                <input type="number" className="input-box" value={editing.price_per_night} onChange={(e) => setEditing({ ...editing, price_per_night: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="text-xs opacity-60">{lang === "mn" ? "Багтаамж" : "Capacity"}</label>
                <input type="number" className="input-box" value={editing.capacity} onChange={(e) => setEditing({ ...editing, capacity: parseInt(e.target.value) || 1 })} />
              </div>
            </div>
            <div>
              <label className="text-xs opacity-60">{lang === "mn" ? "Зураг" : "Image"}</label>
              <input type="file" accept="image/*" onChange={onUpload} className="block mt-1 text-sm" data-testid="house-image" />
              {editing.image_base64 && (
                <img src={`data:image/jpeg;base64,${editing.image_base64}`} alt="preview" className="mt-2 max-h-40 rounded-lg" />
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} data-testid="save-house-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Features CRUD ----
const EMPTY_FEATURE = { name_mn: "", name_en: "", icon: "sparkles", price: 0, active: true };

// Curated quick-add presets — the new outdoor service icons requested by the owner
const QUICK_ADD_PRESETS = [
  "fire-pit",
  "hot-tub",
  "bbq",
  "picnic",
  "stargazing",
  "eco-sanitation",
  "outdoor-shower",
  "cinema",
];

export function FeaturesTab({ lang }) {
  const { push: toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/features");
    setItems(r.data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      const payload = { ...editing };
      delete payload.id;
      delete payload.created_at;
      if (editing.id) {
        await api.put(`/admin/features/${editing.id}`, payload);
      } else {
        await api.post("/admin/features", payload);
      }
      toast(lang === "mn" ? "Хадгаллаа" : "Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "mn" ? "Устгах уу?" : "Delete?")) return;
    await api.delete(`/admin/features/${id}`);
    load();
  };

  const quickAdd = async (iconKey) => {
    const preset = FEATURE_ICONS.find((f) => f.key === iconKey);
    if (!preset) return;
    // Avoid duplicate quick-adds of the same icon
    const exists = items.some((it) => it.icon === preset.key);
    if (exists) {
      toast(
        lang === "mn"
          ? `"${preset.label_mn}" аль хэдийн нэмэгдсэн байна`
          : `"${preset.label_en}" is already added`,
        "error"
      );
      return;
    }
    try {
      await api.post("/admin/features", {
        name_mn: preset.label_mn,
        name_en: preset.label_en,
        icon: preset.key,
        price: 0,
        active: true,
      });
      toast(lang === "mn" ? "Нэмэгдлээ" : "Added");
      load();
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const renderIcon = (key, size = 16) => {
    const Icon = (FEATURE_ICONS.find((f) => f.key === key) || FEATURE_ICONS[5]).icon;
    return <Icon size={size} />;
  };

  return (
    <div className="space-y-6" data-testid="admin-features-tab">
      {/* Quick-add presets */}
      <div className="card p-4 md:p-5" data-testid="features-quick-add">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-xs tracking-widest uppercase opacity-60 mb-1">
              {lang === "mn" ? "Шуурхай нэмэх" : "Quick add"}
            </div>
            <div className="text-sm opacity-70">
              {lang === "mn"
                ? "Гадаа кэмпингийн стандарт үйлчилгээнүүдийг нэг товчоор нэмнэ."
                : "One-click add common outdoor camping services."}
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setEditing({ ...EMPTY_FEATURE })}
            data-testid="add-feature-btn"
          >
            + {lang === "mn" ? "Бусад нэмэх" : "Custom feature"}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {QUICK_ADD_PRESETS.map((key) => {
            const preset = FEATURE_ICONS.find((f) => f.key === key);
            if (!preset) return null;
            const alreadyAdded = items.some((it) => it.icon === preset.key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => quickAdd(key)}
                disabled={alreadyAdded}
                className="flex items-center gap-2.5 p-3 rounded-xl border transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[color:var(--muted)]"
                style={{ borderColor: "var(--border)", background: "var(--card)" }}
                data-testid={`quick-add-${key}`}
                title={alreadyAdded ? (lang === "mn" ? "Аль хэдийн нэмэгдсэн" : "Already added") : ""}
              >
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "var(--secondary)", color: "var(--primary)" }}
                >
                  {renderIcon(key, 16)}
                </span>
                <span className="text-sm leading-tight">
                  {lang === "mn" ? preset.label_mn : preset.label_en}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Existing features list */}
      <div className="grid md:grid-cols-3 gap-3">
        {items.map((f) => (
          <div
            key={f.id}
            className="card p-4 flex items-center justify-between gap-3"
            data-testid={`admin-feature-${f.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
              >
                {renderIcon(f.icon, 16)}
              </span>
              <div className="min-w-0">
                <div className="font-serif text-lg truncate" title={lang === "mn" ? f.name_mn : f.name_en}>
                  {lang === "mn" ? f.name_mn : f.name_en}
                </div>
                <div className="text-xs opacity-60 mt-0.5 truncate">
                  {f.icon} · {(f.price || 0).toLocaleString()} ₮
                </div>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setEditing({ ...f })}
                data-testid={`edit-feature-${f.id}`}
              >
                {lang === "mn" ? "Засах" : "Edit"}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => del(f.id)}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => setEditing(null)}
        >
          <div
            className="card max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-2xl">
              {editing.id
                ? (lang === "mn" ? "Үйлчилгээ засах" : "Edit feature")
                : (lang === "mn" ? "Шинэ үйлчилгээ" : "New feature")}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs opacity-60">Name (MN)</label>
                <input
                  className="input-box"
                  value={editing.name_mn}
                  onChange={(e) => setEditing({ ...editing, name_mn: e.target.value })}
                  data-testid="feature-name-mn"
                />
              </div>
              <div>
                <label className="text-xs opacity-60">Name (EN)</label>
                <input
                  className="input-box"
                  value={editing.name_en}
                  onChange={(e) => setEditing({ ...editing, name_en: e.target.value })}
                  data-testid="feature-name-en"
                />
              </div>
            </div>

            {/* Visual icon picker */}
            <div>
              <label className="text-xs opacity-60 block mb-2">Icon</label>
              <div
                className="grid grid-cols-6 sm:grid-cols-8 gap-2 max-h-56 overflow-y-auto p-2 rounded-xl border"
                style={{ borderColor: "var(--border)", background: "var(--muted)" }}
                data-testid="feature-icon-picker"
              >
                {FEATURE_ICONS.map((opt) => {
                  const active = editing.icon === opt.key;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() =>
                        setEditing({
                          ...editing,
                          icon: opt.key,
                          name_mn: editing.name_mn || opt.label_mn,
                          name_en: editing.name_en || opt.label_en,
                        })
                      }
                      title={`${opt.label_en} (${opt.key})`}
                      className="aspect-square rounded-lg flex items-center justify-center transition-transform hover:scale-105"
                      style={{
                        background: active ? "var(--primary)" : "var(--card)",
                        color: active ? "var(--primary-fg)" : "var(--fg)",
                        border: "1px solid " + (active ? "var(--primary)" : "var(--border)"),
                      }}
                      data-testid={`icon-option-${opt.key}`}
                    >
                      <Icon size={16} />
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] opacity-60 mt-1">
                {lang === "mn"
                  ? "Дүрсийг сонгоход нэр автоматаар санал болгоно."
                  : "Selecting an icon suggests a default name."}
              </div>
            </div>

            <div>
              <label className="text-xs opacity-60">Price (₮)</label>
              <input
                type="number"
                className="input-box"
                value={editing.price}
                onChange={(e) => setEditing({ ...editing, price: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>
                {lang === "mn" ? "Болих" : "Cancel"}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={save}
                data-testid="save-feature-btn"
              >
                {lang === "mn" ? "Хадгалах" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Gallery Photos (with dynamic layout configuration) ----
export function GalleryTab({ lang, settings, refresh }) {
  const { push: toast } = useToast();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingLayout, setSavingLayout] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const layoutMode = settings?.gallery_layout_mode || "bento";

  const load = async () => {
    const r = await api.get("/gallery");
    setItems(r.data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const b64 = await fileToBase64(f);
      await api.post("/admin/gallery", {
        image_base64: b64,
        caption_mn: "",
        caption_en: "",
        order: items.length,
        tile_size: "medium",
      });
      await load();
      toast(lang === "mn" ? "Зураг нэмэгдлээ" : "Photo added");
    } catch (err) {
      toast(err.message || formatApiErrorDetail(err.response?.data?.detail), "error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "mn" ? "Устгах уу?" : "Delete?")) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((x) => x.id === id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    setItems(arr); // optimistic
    try {
      await api.put("/admin/gallery/reorder", { ids: arr.map((x) => x.id) });
    } catch (err) {
      load();
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const quickSetSize = async (id, tile_size) => {
    const target = items.find((x) => x.id === id);
    if (!target) return;
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, tile_size } : x)));
    try {
      await api.put(`/admin/gallery/${id}`, {
        caption_mn: target.caption_mn || "",
        caption_en: target.caption_en || "",
        order: target.order || 0,
        tile_size,
      });
    } catch (err) {
      load();
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const saveEditing = async () => {
    if (!editing) return;
    try {
      await api.put(`/admin/gallery/${editing.id}`, {
        caption_mn: editing.caption_mn || "",
        caption_en: editing.caption_en || "",
        order: editing.order || 0,
        tile_size: editing.tile_size || "medium",
      });
      setEditing(null);
      load();
      toast(lang === "mn" ? "Хадгаллаа" : "Saved");
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const setLayoutMode = async (mode) => {
    setSavingLayout(true);
    try {
      await api.put("/admin/settings", { gallery_layout_mode: mode });
      await refresh?.();
      toast(lang === "mn" ? "Layout шинэчлэгдлээ" : "Layout updated");
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    } finally {
      setSavingLayout(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery-tab">
      {/* Layout mode + actions header */}
      <div className="card p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs tracking-widest uppercase opacity-60 mb-1.5">
            {lang === "mn" ? "Хэвлэлийн горим" : "Layout mode"}
          </div>
          <div className="inline-flex rounded-full border border-[color:var(--border)] overflow-hidden" data-testid="gallery-layout-mode">
            <button
              type="button"
              onClick={() => setLayoutMode("bento")}
              disabled={savingLayout}
              className="flex items-center gap-2 px-4 py-2 text-sm transition-colors"
              style={{
                background: layoutMode === "bento" ? "var(--primary)" : "transparent",
                color: layoutMode === "bento" ? "var(--primary-fg)" : "var(--fg)",
              }}
              data-testid="layout-mode-bento"
            >
              <LayoutGrid size={14} /> {lang === "mn" ? "Бенто" : "Bento"}
            </button>
            <button
              type="button"
              onClick={() => setLayoutMode("uniform")}
              disabled={savingLayout}
              className="flex items-center gap-2 px-4 py-2 text-sm transition-colors border-l border-[color:var(--border)]"
              style={{
                background: layoutMode === "uniform" ? "var(--primary)" : "transparent",
                color: layoutMode === "uniform" ? "var(--primary-fg)" : "var(--fg)",
              }}
              data-testid="layout-mode-uniform"
            >
              <Grid3x3 size={14} /> {lang === "mn" ? "Жигд тор" : "Uniform"}
            </button>
          </div>
          <div className="text-xs opacity-60 mt-2 max-w-md">
            {lang === "mn"
              ? "Бенто: тус бүрийн хэмжээг сонгож, олон хэмжээст урлагийн зохиомж үүсгэнэ. Жигд тор: бүх зураг ижил хэмжээтэй."
              : "Bento: pick a size per photo for an editorial-style grid. Uniform: every tile renders at the same size."}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setPreviewMode((p) => !p)}
            className={`btn btn-sm ${previewMode ? "btn-primary" : "btn-ghost"}`}
            data-testid="gallery-preview-toggle"
          >
            {previewMode
              ? (lang === "mn" ? "Засварлах" : "Edit mode")
              : (lang === "mn" ? "Урьдчилан харах" : "Preview")}
          </button>
          <label className="btn btn-primary btn-sm cursor-pointer">
            {uploading
              ? (lang === "mn" ? "Ачааллаж байна..." : "Uploading...")
              : `+ ${lang === "mn" ? "Зураг" : "Photo"}`}
            <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="gallery-upload-input" />
          </label>
        </div>
      </div>

      {items.length === 0 && (
        <div className="card p-10 text-center opacity-70" data-testid="gallery-empty">
          {lang === "mn" ? "Зураг байхгүй. Эхний зургийг оруулна уу." : "No photos yet. Upload your first one."}
        </div>
      )}

      {/* Preview mode — render exactly like the public bento grid */}
      {previewMode && items.length > 0 && (
        <div
          className="grid grid-cols-2 md:grid-cols-12 auto-rows-[140px] md:auto-rows-[180px] gap-3"
          data-testid="gallery-preview-grid"
        >
          {items.map((p, i) => {
            const span =
              layoutMode === "uniform"
                ? "md:col-span-4"
                : tileSpan(p.tile_size || "medium");
            return (
              <div
                key={p.id}
                className={`relative overflow-hidden rounded-2xl col-span-2 ${span}`}
                data-testid={`preview-tile-${i}`}
              >
                <img
                  src={`data:image/jpeg;base64,${p.image_base64}`}
                  alt={p.caption_en || ""}
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/55 text-white tracking-widest uppercase">
                  #{i + 1} · {p.tile_size || "medium"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit mode — cards with controls */}
      {!previewMode && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-edit-grid">
          {items.map((p, i) => {
            const currentSize = p.tile_size || "medium";
            return (
              <div key={p.id} className="card overflow-hidden flex flex-col" data-testid={`admin-gallery-photo-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[color:var(--muted)] relative">
                  <img
                    src={`data:image/jpeg;base64,${p.image_base64}`}
                    alt={p.caption_en || ""}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/55 text-white tracking-widest uppercase font-mono">
                    #{i + 1}
                  </span>
                </div>
                <div className="p-3 space-y-3">
                  {/* Captions */}
                  <div className="text-xs opacity-70 truncate" title={p.caption_mn || p.caption_en || ""}>
                    {p.caption_mn || p.caption_en || (lang === "mn" ? "(тайлбаргүй)" : "(no caption)")}
                  </div>

                  {/* Tile size segmented control */}
                  <div>
                    <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5">
                      {lang === "mn" ? "Хэмжээ" : "Tile size"}
                    </div>
                    <div className="flex flex-wrap gap-1" data-testid={`tile-size-${p.id}`}>
                      {TILE_SIZE_OPTIONS.map((opt) => {
                        const active = currentSize === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => quickSetSize(p.id, opt.value)}
                            className="px-2.5 py-1 rounded-full text-[11px] transition-colors"
                            style={{
                              background: active ? "var(--primary)" : "var(--muted)",
                              color: active ? "var(--primary-fg)" : "var(--fg)",
                              border: "1px solid " + (active ? "var(--primary)" : "var(--border)"),
                            }}
                            data-testid={`tile-size-${p.id}-${opt.value}`}
                          >
                            {opt[`label_${lang}`]}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between gap-1 pt-1 border-t border-[color:var(--border)]/50">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => move(p.id, -1)}
                        disabled={i === 0}
                        className="w-8 h-8 rounded-full flex items-center justify-center border border-[color:var(--border)] disabled:opacity-30 hover:bg-[color:var(--muted)] transition"
                        title={lang === "mn" ? "Дээш" : "Move up"}
                        data-testid={`move-up-${p.id}`}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(p.id, 1)}
                        disabled={i === items.length - 1}
                        className="w-8 h-8 rounded-full flex items-center justify-center border border-[color:var(--border)] disabled:opacity-30 hover:bg-[color:var(--muted)] transition"
                        title={lang === "mn" ? "Доош" : "Move down"}
                        data-testid={`move-down-${p.id}`}
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing({ ...p, tile_size: p.tile_size || "medium" })}
                        className="w-8 h-8 rounded-full flex items-center justify-center border border-[color:var(--border)] hover:bg-[color:var(--muted)] transition"
                        title={lang === "mn" ? "Засах" : "Edit caption"}
                        data-testid={`edit-photo-${p.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => del(p.id)}
                        className="w-8 h-8 rounded-full flex items-center justify-center border border-[color:var(--border)] hover:bg-red-50 hover:text-red-700 transition"
                        title={lang === "mn" ? "Устгах" : "Delete"}
                        data-testid={`delete-photo-${p.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit caption modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => setEditing(null)}
        >
          <div
            className="card max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="photo-edit-modal"
          >
            <h3 className="font-serif text-2xl">
              {lang === "mn" ? "Зургийн тайлбар" : "Photo caption"}
            </h3>
            <div className="aspect-[16/9] overflow-hidden rounded-xl bg-[color:var(--muted)]">
              <img
                src={`data:image/jpeg;base64,${editing.image_base64}`}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <label className="text-xs opacity-60 block mb-1">Caption (MN)</label>
              <input
                className="input-box"
                value={editing.caption_mn || ""}
                onChange={(e) => setEditing({ ...editing, caption_mn: e.target.value })}
                data-testid="edit-caption-mn"
              />
            </div>
            <div>
              <label className="text-xs opacity-60 block mb-1">Caption (EN)</label>
              <input
                className="input-box"
                value={editing.caption_en || ""}
                onChange={(e) => setEditing({ ...editing, caption_en: e.target.value })}
                data-testid="edit-caption-en"
              />
            </div>
            <div>
              <label className="text-xs opacity-60 block mb-1">
                {lang === "mn" ? "Хэмжээ" : "Tile size"}
              </label>
              <div className="flex flex-wrap gap-1">
                {TILE_SIZE_OPTIONS.map((opt) => {
                  const active = (editing.tile_size || "medium") === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setEditing({ ...editing, tile_size: opt.value })}
                      className="px-2.5 py-1 rounded-full text-[11px]"
                      style={{
                        background: active ? "var(--primary)" : "var(--muted)",
                        color: active ? "var(--primary-fg)" : "var(--fg)",
                        border: "1px solid " + (active ? "var(--primary)" : "var(--border)"),
                      }}
                    >
                      {opt[`label_${lang}`]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>
                {lang === "mn" ? "Болих" : "Cancel"}
              </button>
              <button className="btn btn-primary btn-sm" onClick={saveEditing} data-testid="save-photo-btn">
                {lang === "mn" ? "Хадгалах" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Settings Tab ----
export function SettingsTab({ lang, settings, refresh }) {
  const { push: toast } = useToast();
  const [form, setForm] = useState({});

  useEffect(() => {
    setForm(settings || {});
  }, [settings]);

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast(lang === "mn" ? "Хадгаллаа" : "Saved");
      refresh();
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail), "error");
    }
  };

  const onQrUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const b64 = await fileToBase64(f);
      setForm({ ...form, socialpay_qr_base64: b64 });
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const onHeroUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      // Hero is shown big — allow up to ~5MB
      const b64 = await fileToBase64(f, 5 * 1024 * 1024);
      // Uploading a file takes precedence over a previously set URL
      setForm({ ...form, hero_image_base64: b64, hero_image_url: "" });
    } catch (err) {
      toast(err.message, "error");
    } finally {
      e.target.value = "";
    }
  };

  const clearHero = () => {
    setForm({ ...form, hero_image_base64: "", hero_image_url: "" });
  };

  const heroPreview = form.hero_image_base64
    ? `data:image/jpeg;base64,${form.hero_image_base64}`
    : form.hero_image_url || "";

  const field = (key, label, type = "text") => (
    <div>
      <label className="text-xs opacity-60 block mb-1">{label}</label>
      {type === "textarea" ? (
        <textarea rows={3} className="input-box" value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      ) : (
        <input type={type} className="input-box" value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`settings-${key}`} />
      )}
    </div>
  );

  const colorField = (key, label) => (
    <div>
      <label className="text-xs opacity-60 block mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={form[key] || "#000000"} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="w-12 h-10 rounded cursor-pointer" data-testid={`color-${key}`} />
        <input type="text" value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="input-box flex-1 font-mono text-xs" />
      </div>
    </div>
  );

  return (
    <div className="space-y-8 max-w-3xl" data-testid="admin-settings-tab">
      {/* Site content */}
      <div className="card p-6 space-y-4">
        <h3 className="font-serif text-2xl">{lang === "mn" ? "Сайтын агуулга" : "Site content"}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {field("site_title_mn", "Title (MN)")}
          {field("site_title_en", "Title (EN)")}
          {field("tagline_mn", "Tagline (MN)")}
          {field("tagline_en", "Tagline (EN)")}
        </div>
        {field("about_mn", "About (MN)", "textarea")}
        {field("about_en", "About (EN)", "textarea")}
      </div>

      {/* Hero image */}
      <div className="card p-6 space-y-4" data-testid="hero-image-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-serif text-2xl">{lang === "mn" ? "Нүүр зураг" : "Hero image"}</h3>
          {heroPreview && (
            <button
              type="button"
              onClick={clearHero}
              className="btn btn-ghost btn-sm"
              data-testid="hero-clear-btn"
            >
              <Trash2 size={14} /> {lang === "mn" ? "Арилгах" : "Clear"}
            </button>
          )}
        </div>
        <p className="text-sm opacity-70">
          {lang === "mn"
            ? "Нүүр хуудасны том зураг. Файл оруулах эсвэл URL хаяг ашиглана. Файл оруулсан тохиолдолд URL хүчингүй болно."
            : "The large image at the top of the homepage. Upload a file or paste a URL — uploading overrides the URL."}
        </p>

        <div className="grid md:grid-cols-2 gap-5 items-start">
          {/* Preview */}
          <div
            className="aspect-[16/10] rounded-2xl overflow-hidden border border-[color:var(--border)] flex items-center justify-center"
            style={{ background: "var(--muted)" }}
            data-testid="hero-preview"
          >
            {heroPreview ? (
              <img
                src={heroPreview}
                alt="Hero preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xs opacity-60 text-center px-6">
                {lang === "mn"
                  ? "Зураг сонгогдоогүй байна"
                  : "No hero image selected"}
              </span>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-3">
            <label className="btn btn-primary btn-sm cursor-pointer w-full sm:w-auto">
              + {lang === "mn" ? "Файл оруулах" : "Upload file"}
              <input
                type="file"
                accept="image/*"
                onChange={onHeroUpload}
                className="hidden"
                data-testid="hero-upload-input"
              />
            </label>
            <div className="text-[11px] opacity-60">
              {lang === "mn"
                ? "JPG, PNG, WebP. 5MB-аас бага."
                : "JPG, PNG, WebP. Up to 5MB."}
            </div>
            <div className="pt-3 border-t border-[color:var(--border)]/60">
              <label className="text-xs opacity-60 block mb-1">
                {lang === "mn"
                  ? "Эсвэл URL ашиглах"
                  : "Or use a URL"}
              </label>
              <input
                className="input-box"
                placeholder="https://..."
                value={form.hero_image_url || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    hero_image_url: e.target.value,
                    // When admin types a URL, drop a previously uploaded file
                    hero_image_base64: e.target.value ? "" : form.hero_image_base64,
                  })
                }
                data-testid="settings-hero_image_url"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="card p-6 space-y-4">
        <h3 className="font-serif text-2xl">{lang === "mn" ? "Холбоо барих" : "Contact info"}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {field("contact_phone", "Phone")}
          {field("contact_email", "Email")}
          {field("contact_address_mn", "Address (MN)")}
          {field("contact_address_en", "Address (EN)")}
        </div>
        {field("map_embed_url", "Map embed URL")}
      </div>

      {/* Theme */}
      <div className="card p-6 space-y-4">
        <h3 className="font-serif text-2xl">{lang === "mn" ? "Өнгөний тохиргоо" : "Theme colors"}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {colorField("theme_primary", "Primary")}
          {colorField("theme_secondary", "Secondary")}
          {colorField("theme_accent", "Accent")}
          {colorField("theme_background", "Background")}
          {colorField("theme_foreground", "Text color")}
        </div>
      </div>

      {/* SocialPay */}
      <div className="card p-6 space-y-4">
        <h3 className="font-serif text-2xl">SocialPay QR</h3>
        <p className="text-sm opacity-70">{lang === "mn" ? "Golomt SocialPay-н QR зургийг оруулна уу" : "Upload the Golomt SocialPay QR image"}</p>
        <input type="file" accept="image/*" onChange={onQrUpload} className="text-sm" data-testid="qr-upload-input" />
        {form.socialpay_qr_base64 && (
          <img src={`data:image/png;base64,${form.socialpay_qr_base64}`} alt="QR" className="max-w-[220px] rounded-lg border border-[color:var(--border)]" />
        )}
      </div>

      <button onClick={save} className="btn btn-primary" data-testid="save-settings-btn">
        {lang === "mn" ? "Хадгалах" : "Save changes"}
      </button>
    </div>
  );
}

// ---- Contacts Tab ----
export function ContactsTab({ lang }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/contacts");
    setItems(r.data || []);
  };
  useEffect(() => {
    load();
  }, []);
  const del = async (id) => {
    if (!window.confirm(lang === "mn" ? "Устгах уу?" : "Delete?")) return;
    await api.delete(`/admin/contacts/${id}`);
    load();
  };
  return (
    <div className="space-y-3" data-testid="admin-contacts-tab">
      {items.map((c) => (
        <div key={c.id} className="card p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium">{c.name} <span className="text-xs opacity-60 ml-2">{c.email} {c.phone && `· ${c.phone}`}</span></div>
              <p className="mt-2 text-sm opacity-90 whitespace-pre-wrap">{c.message}</p>
              <div className="text-xs opacity-50 mt-2">{new Date(c.created_at).toLocaleString()}</div>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => del(c.id)}>
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="opacity-60 text-sm">{lang === "mn" ? "Шинэ зурвас байхгүй" : "No messages"}</div>}
    </div>
  );
}
